/**
 * @krisspy-file
 * @type page
 * @name "JourneyMapPage"
 * @title "Journey Map"
 * @description "Hlavní mapa cesty s 5 fázemi, digitálními a fyzickými touchpointy a nápady. Drag & drop, export PDF."
 * @routes ["/journey/:type", "/journey-mapping"]
 * @pathExample "/journey/customer"
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCorners,
  UniqueIdentifier,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Globe,
  Store,
  Lightbulb,
  ArrowRight,
  Plus,
  Printer,
  ArrowLeft,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';

import {
  MOCK_JOURNEYS,
  JourneyType,
  MockTouchpoint,
  MockIdea,
  MockPhase,
  createEmptyJourneyState,
} from '../lib/mockData';
import TouchpointCard from '../components/TouchpointCard';
import IdeaCard from '../components/IdeaCard';
import AddTouchpointModal from '../components/AddTouchpointModal';
import AddIdeaModal from '../components/AddIdeaModal';
import AssignIdeaModal from '../components/AssignIdeaModal';

// ─── Droppable column wrapper ─────────────────────────────────────────────────

const DroppableColumn: React.FC<{ id: string; children: React.ReactNode; className?: string }> = ({
  id, children, className = ''
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`${className} ${isOver ? 'ring-2 ring-indigo-300 ring-inset rounded-xl' : ''}`}>
      {children}
    </div>
  );
};

// ─── Sortable Touchpoint ──────────────────────────────────────────────────────

const SortableTouchpoint: React.FC<{
  touchpoint: MockTouchpoint;
  phaseColor: string;
  phaseBg: string;
  onToggleInfluence: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
}> = ({ touchpoint, phaseColor, phaseBg, onToggleInfluence, onDelete, onRename }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: touchpoint.id });

  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}>
      <TouchpointCard
        id={touchpoint.id}
        title={touchpoint.title}
        type={touchpoint.type}
        canInfluence={touchpoint.canInfluence}
        phaseColor={phaseColor}
        phaseBg={phaseBg}
        onToggleInfluence={onToggleInfluence}
        onDelete={onDelete}
        onRename={onRename}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

// ─── Sortable Idea ────────────────────────────────────────────────────────────

const SortableIdea: React.FC<{
  idea: MockIdea;
  onClickMobile?: () => void;
}> = ({ idea, onClickMobile }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: idea.id });

  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <IdeaCard
        id={idea.id}
        title={idea.title}
        source={idea.source}
        isDraggable
        onClick={onClickMobile}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

type ModalState =
  | { kind: 'none' }
  | { kind: 'touchpoint'; phaseId: string; defaultType: 'digital' | 'physical' }
  | { kind: 'idea'; phaseId: string }
  | { kind: 'assignIdea'; ideaId: string; ideaTitle: string };

/**
 * Načte všechny ideas z DB pro fáze v daném empty state a sloučí je
 * s mockData ideas. mockData jsou základ (templates), DB ideas jsou
 * "schválené nápady od uživatelů" - přidávají se na konec listu.
 *
 * Deduplikace: pokud DB idea má stejný title jako mockData idea,
 * mockData verze vyhrává (nepřidávat duplikát).
 */
async function mergeApprovedIdeasFromDb(empty: MockPhase[]): Promise<MockPhase[]> {
  try {
    const phaseIds = empty.map(p => p.id);
    const { data, error } = await supabase
      .from('ideas')
      .select('id, title, phase_id')
      .in('phase_id', phaseIds);

    if (error || !data) return empty;

    // Group DB ideas by phase_id
    const byPhase = new Map<string, Array<{ id: string; title: string }>>();
    for (const row of data as Array<{ id: string; title: string; phase_id: string }>) {
      if (!byPhase.has(row.phase_id)) byPhase.set(row.phase_id, []);
      byPhase.get(row.phase_id)!.push({ id: row.id, title: row.title });
    }

    return empty.map(phase => {
      const dbIdeas = byPhase.get(phase.id) ?? [];
      // Existing mock titles (lowercase) for dedup
      const existingTitles = new Set(phase.ideas.map(i => i.title.toLowerCase().trim()));
      const additional: MockIdea[] = dbIdeas
        .filter(d => !existingTitles.has(d.title.toLowerCase().trim()))
        .map(d => ({ id: d.id, title: d.title, source: 'global' as const }));
      return { ...phase, ideas: [...phase.ideas, ...additional] };
    });
  } catch (err) {
    console.warn('[mergeApprovedIdeasFromDb] failed', err);
    return empty;
  }
}

const JourneyMapPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const journeyType = (type as JourneyType) || 'customer';
  const journeyDef = MOCK_JOURNEYS[journeyType] ?? MOCK_JOURNEYS.customer;
  const mapRef = useRef<HTMLDivElement>(null);

  // ─── Persisted state ──
  // phases starts empty until we load from Supabase / localStorage / initial empty.
  const [phases, setPhases] = useState<MockPhase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  // localStorage key for offline / mock mode
  const storageKey = `journey_state__${journeyType}`;

  // ─── Initial load ──
  // Loads user data from Supabase (if logged in) or from localStorage (mock mode).
  // For brand-new users, initializes empty phases (only ideas as inspiration, no touchpoints).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);

      // Supabase mode
      if (user && isSupabaseConfigured) {
        try {
          const { data, error } = await supabase
            .from('journey_user_data')
            .select('phases')
            .eq('user_id', user.id)
            .eq('journey_type', journeyType)
            .maybeSingle();

          if (cancelled) return;
          if (error) throw error;

          if (data?.phases && Array.isArray(data.phases) && (data.phases as any[]).length > 0) {
            setPhases(data.phases as MockPhase[]);
          } else {
            // First time visiting this journey — start empty + merge approved ideas from DB
            const empty = createEmptyJourneyState(journeyType);
            const merged = await mergeApprovedIdeasFromDb(empty);
            if (!cancelled) setPhases(merged);
          }
        } catch (err) {
          console.error('[JourneyMapPage] load failed, falling back to empty state', err);
          setPhases(createEmptyJourneyState(journeyType));
        } finally {
          if (!cancelled) setIsLoading(false);
        }
        return;
      }

      // Mock / offline mode — localStorage
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setPhases(parsed as MockPhase[]);
            setIsLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('[JourneyMapPage] localStorage parse failed', err);
      }
      setPhases(createEmptyJourneyState(journeyType));
      setIsLoading(false);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyType, user?.id]);

  // ─── Auto-save (debounced) ──
  // Saves to Supabase or localStorage 800ms after the last change.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoading || phases.length === 0) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus('saving');

      // Supabase mode
      if (user && isSupabaseConfigured) {
        try {
          const { error } = await supabase
            .from('journey_user_data')
            .upsert(
              {
                user_id: user.id,
                journey_type: journeyType,
                phases: phases as any,
              },
              { onConflict: 'user_id,journey_type' }
            );
          if (error) throw error;
          setSaveStatus('saved');
        } catch (err) {
          console.error('[JourneyMapPage] auto-save failed', err);
          setSaveStatus('error');
        }
      } else {
        // Mock mode — localStorage
        try {
          localStorage.setItem(storageKey, JSON.stringify(phases));
          setSaveStatus('saved');
        } catch (err) {
          console.error('[JourneyMapPage] localStorage save failed', err);
          setSaveStatus('error');
        }
      }

      // Auto-hide "Saved" indicator after 2s
      if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
      savedStatusTimerRef.current = setTimeout(() => {
        setSaveStatus(prev => (prev === 'saved' ? 'idle' : prev));
      }, 2000);
    }, 800);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases, isLoading]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // ─── Find item location ──

  type ItemLocation =
    | { phaseId: string; column: 'digital' | 'physical' | 'ideas'; index: number }
    | null;

  const findItem = useCallback(
    (id: UniqueIdentifier): ItemLocation => {
      for (const phase of phases) {
        const dIdx = phase.digitalTouchpoints.findIndex(t => t.id === id);
        if (dIdx !== -1) return { phaseId: phase.id, column: 'digital', index: dIdx };
        const pIdx = phase.physicalTouchpoints.findIndex(t => t.id === id);
        if (pIdx !== -1) return { phaseId: phase.id, column: 'physical', index: pIdx };
        const iIdx = phase.ideas.findIndex(i => i.id === id);
        if (iIdx !== -1) return { phaseId: phase.id, column: 'ideas', index: iIdx };
      }
      return null;
    },
    [phases]
  );

  // ─── Find droppable column ──
  const parseDroppableId = (id: string): { phaseId: string; column: 'digital' | 'physical' } | null => {
    const digitalMatch = id.match(/^col-digital-(.+)$/);
    if (digitalMatch) return { phaseId: digitalMatch[1], column: 'digital' };
    const physicalMatch = id.match(/^col-physical-(.+)$/);
    if (physicalMatch) return { phaseId: physicalMatch[1], column: 'physical' };
    return null;
  };

  const handleDragStart = ({ active }: DragStartEvent) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over) return;

    const from = findItem(active.id);
    if (!from) return;

    // Case 1: dropped on a droppable container (idea → touchpoint column)
    const droppable = parseDroppableId(String(over.id));
    if (droppable && from.column === 'ideas') {
      setPhases(prev => {
        let ideaToConvert: MockIdea | undefined;
        const withoutIdea = prev.map(phase => ({
          ...phase,
          ideas: phase.ideas.filter(i => {
            if (i.id === active.id) { ideaToConvert = i; return false; }
            return true;
          }),
        }));
        if (!ideaToConvert) return prev;
        const newTp: MockTouchpoint = {
          id: `tp-from-idea-${Date.now()}`,
          title: ideaToConvert.title,
          type: droppable.column,
          canInfluence: true,
          isCustom: true,
        };
        return withoutIdea.map(phase => {
          if (phase.id !== droppable.phaseId) return phase;
          if (droppable.column === 'digital')
            return { ...phase, digitalTouchpoints: [...phase.digitalTouchpoints, newTp] };
          return { ...phase, physicalTouchpoints: [...phase.physicalTouchpoints, newTp] };
        });
      });
      return;
    }

    // Case 2: dropped on another item
    const to = findItem(over.id);
    if (!to) return;

    if (from.column === 'ideas' && to.column !== 'ideas') {
      setPhases(prev => {
        let ideaToConvert: MockIdea | undefined;
        const withoutIdea = prev.map(phase => ({
          ...phase,
          ideas: phase.ideas.filter(i => {
            if (i.id === active.id) { ideaToConvert = i; return false; }
            return true;
          }),
        }));
        if (!ideaToConvert) return prev;
        const newTp: MockTouchpoint = {
          id: `tp-from-idea-${Date.now()}`,
          title: ideaToConvert.title,
          type: to.column as 'digital' | 'physical',
          canInfluence: true,
          isCustom: true,
        };
        return withoutIdea.map(phase => {
          if (phase.id !== to.phaseId) return phase;
          if (to.column === 'digital') {
            const arr = [...phase.digitalTouchpoints];
            arr.splice(to.index, 0, newTp);
            return { ...phase, digitalTouchpoints: arr };
          }
          const arr = [...phase.physicalTouchpoints];
          arr.splice(to.index, 0, newTp);
          return { ...phase, physicalTouchpoints: arr };
        });
      });
      return;
    }

    if (active.id === over.id) return;

    if (from.phaseId === to.phaseId && from.column === to.column) {
      setPhases(prev =>
        prev.map(phase => {
          if (phase.id !== from.phaseId) return phase;
          const reorder = (arr: any[]) => {
            const a = [...arr];
            const [item] = a.splice(from.index, 1);
            a.splice(to.index, 0, item);
            return a;
          };
          if (from.column === 'digital') return { ...phase, digitalTouchpoints: reorder(phase.digitalTouchpoints) };
          if (from.column === 'physical') return { ...phase, physicalTouchpoints: reorder(phase.physicalTouchpoints) };
          return { ...phase, ideas: reorder(phase.ideas) };
        })
      );
    }
  };

  // ─── Touchpoint operations ──

  const handleAddTouchpoint = (name: string, type: 'digital' | 'physical', phaseId: string) => {
    const newTp: MockTouchpoint = {
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      title: name,
      type,
      canInfluence: true,
      isCustom: true,
    };
    setPhases(prev =>
      prev.map(phase => {
        if (phase.id !== phaseId) return phase;
        if (type === 'digital') return { ...phase, digitalTouchpoints: [...phase.digitalTouchpoints, newTp] };
        return { ...phase, physicalTouchpoints: [...phase.physicalTouchpoints, newTp] };
      })
    );
    setModal({ kind: 'none' });
  };

  const handleToggleInfluence = (phaseId: string, touchpointId: string) => {
    setPhases(prev =>
      prev.map(phase => {
        if (phase.id !== phaseId) return phase;
        const toggle = (arr: MockTouchpoint[]) =>
          arr.map(t => t.id === touchpointId ? { ...t, canInfluence: !t.canInfluence } : t);
        return { ...phase, digitalTouchpoints: toggle(phase.digitalTouchpoints), physicalTouchpoints: toggle(phase.physicalTouchpoints) };
      })
    );
  };

  const handleDeleteTouchpoint = (phaseId: string, touchpointId: string) => {
    setPhases(prev =>
      prev.map(phase => {
        if (phase.id !== phaseId) return phase;
        return {
          ...phase,
          digitalTouchpoints: phase.digitalTouchpoints.filter(t => t.id !== touchpointId),
          physicalTouchpoints: phase.physicalTouchpoints.filter(t => t.id !== touchpointId),
        };
      })
    );
  };

  const handleRenameTouchpoint = (phaseId: string, touchpointId: string, title: string) => {
    setPhases(prev =>
      prev.map(phase => {
        if (phase.id !== phaseId) return phase;
        const rename = (arr: MockTouchpoint[]) => arr.map(t => t.id === touchpointId ? { ...t, title } : t);
        return { ...phase, digitalTouchpoints: rename(phase.digitalTouchpoints), physicalTouchpoints: rename(phase.physicalTouchpoints) };
      })
    );
  };

  // ─── Idea operations ──

  const handleAddIdea = async (title: string, description: string, phaseId: string) => {
    // 1) Lokálně přidat hned (user vidí svou změnu okamžitě)
    const newIdea: MockIdea = { id: `user-idea-${Date.now()}`, title, source: 'user', assignedToPhase: undefined };
    setPhases(prev =>
      prev.map(phase => phase.id === phaseId ? { ...phase, ideas: [...phase.ideas, newIdea] } : phase)
    );
    setModal({ kind: 'none' });

    // 2) Odeslat jako suggestion adminovi (pokud user přihlášen)
    if (user && isSupabaseConfigured) {
      try {
        const { error } = await supabase.from('suggestions').insert({
          user_id: user.id,
          suggestion_type: 'idea',
          title,
          description: description || title,
          target_phase_id: phaseId,
          status: 'pending',
        });
        if (error) console.warn('[JourneyMapPage] suggestion submit failed', error);
      } catch (err) {
        console.warn('[JourneyMapPage] suggestion submit exception', err);
      }
    }
  };

  const handleAssignIdea = (ideaId: string, phaseId: string, type: 'digital' | 'physical') => {
    setPhases(prev => {
      let ideaToConvert: MockIdea | undefined;
      const withoutIdea = prev.map(phase => ({
        ...phase,
        ideas: phase.ideas.filter(i => { if (i.id === ideaId) { ideaToConvert = i; return false; } return true; }),
      }));
      if (!ideaToConvert) return prev;
      const newTp: MockTouchpoint = {
        id: `tp-assigned-${Date.now()}`,
        title: ideaToConvert.title,
        type,
        canInfluence: true,
        isCustom: true,
      };
      return withoutIdea.map(phase => {
        if (phase.id !== phaseId) return phase;
        if (type === 'digital') return { ...phase, digitalTouchpoints: [...phase.digitalTouchpoints, newTp] };
        return { ...phase, physicalTouchpoints: [...phase.physicalTouchpoints, newTp] };
      });
    });
    setModal({ kind: 'none' });
  };

  // ─── Export do PDF přes window.print() ──
  // Strategie: použijeme nativní browserový print engine (nikoli žádnou JS
  // image library). Browser umí dokonale renderovat HTML + CSS jako PDF.
  // Injektujeme @media print stylesheet, který skryje všechno UI, jen
  // ponechá samotnou mapu. Uživatel pak v dialogu zvolí "Uložit jako PDF".
  //
  // Toto je 100% spolehlivé - žádné placeholdery, žádné šedé borders,
  // perfektní kvalita (vector-based), funguje v každém browseru.

  const handleExportPDF = () => {
    // 1) Inject print stylesheet (jen jednou, idempotentně)
    const STYLE_ID = 'journey-print-styles';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        @media print {
          /* A3 landscape, minimální okraje */
          @page {
            size: A3 landscape;
            margin: 8mm;
          }

          /* Skryj VŠECHNO mimo journey mapu */
          body * {
            visibility: hidden;
          }

          /* Zviditelní jen mapu a její potomky */
          #journey-print-area,
          #journey-print-area * {
            visibility: visible;
          }

          /* Mapa zabere celou stránku */
          #journey-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }

          /* Skryj VŠECHNY ovládací prvky uvnitř mapy */
          #journey-print-area [data-pdf-hide],
          #journey-print-area [data-section-add],
          #journey-print-area [data-pdf-arrow],
          #journey-print-area [title="Přetáhnout"],
          #journey-print-area [title="Přejmenovat"],
          #journey-print-area [title="Smazat"],
          #journey-print-area [title="Uložit"],
          #journey-print-area [title="Zrušit"],
          #journey-print-area .opacity-0 {
            display: none !important;
          }

          /* Zachovej grafiku karet při tisku (jinak browser stripping color) */
          #journey-print-area * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Dropdrop dashed placeholdery skryj v PDF (matou) */
          #journey-print-area .border-dashed {
            display: none !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // 2) id="journey-print-area" je už natvrdo v JSX

    // 3) Nastav title okna na něco užitečnějšího (název PDF v dialogu)
    const originalTitle = document.title;
    document.title = `Journey Map - ${journeyDef.label}`;

    // 4) Otevři print dialog (browser nabídne "Uložit jako PDF" jako cíl)
    setTimeout(() => {
      window.print();
      // Po dialogu obnov původní title
      setTimeout(() => { document.title = originalTitle; }, 200);
    }, 100);
  };

  // ─── Active item for overlay ──

  const activeItem = activeId ? findItem(activeId) : null;
  const overlayTouchpoint = activeItem && activeItem.column !== 'ideas'
    ? phases.flatMap(p => [...p.digitalTouchpoints, ...p.physicalTouchpoints]).find(t => t.id === activeId)
    : undefined;
  const overlayIdea = activeItem?.column === 'ideas'
    ? phases.flatMap(p => p.ideas).find(i => i.id === activeId)
    : undefined;
  const activePhase = activeItem
    ? phases.find(p => p.id === activeItem.phaseId)
    : undefined;

  const phaseListForAssign = phases.map(p => ({ id: p.id, name: p.name, order: p.order }));

  // ─── Loading state ──
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm">Načítám vaši mapu cesty...</p>
        </div>
      </div>
    );
  }

  // ─── Save status badge ──
  const SaveStatusBadge: React.FC = () => {
    if (saveStatus === 'idle') return null;
    if (saveStatus === 'saving') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 className="w-3 h-3 animate-spin" />
          Ukládám...
        </span>
      );
    }
    if (saveStatus === 'saved') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
          <Check className="w-3 h-3" />
          Uloženo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-red-600" title="Změny se nepodařilo uložit. Zkuste znovu.">
        <AlertCircle className="w-3 h-3" />
        Chyba ukládání
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/journey-select')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Zpět</span>
          </button>
          <div className="w-px h-5 bg-gray-200" />
          <div>
            <h1 className="text-base font-bold text-gray-900">{journeyDef.label} — Journey Map</h1>
            <p className="text-xs text-gray-400">{journeyDef.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <SaveStatusBadge />
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors"
            title="Otevře dialog tisku - v dialogu zvolte 'Uložit jako PDF'"
          >
            <Printer className="w-4 h-4" />
            Tisk / PDF
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-2 flex items-center gap-6 flex-wrap text-xs text-gray-500">
        <div className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-indigo-500" /> Digitální touchpointy</div>
        <div className="flex items-center gap-1.5"><Store className="w-3.5 h-3.5 text-emerald-500" /> Fyzické touchpointy</div>
        <div className="flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5 text-amber-500" /> Nápady (přetáhněte na touchpoint)</div>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex text-xs px-2 py-0.5 bg-indigo-600 text-white rounded-md font-semibold">Mohu ovlivnit</span>
          <span className="text-gray-400">/</span>
          <span className="inline-flex text-xs px-2 py-0.5 border border-gray-300 text-gray-500 rounded-md font-semibold">Nemohu ovlivnit</span>
        </div>
      </div>

      {/* Map scrollable area */}
      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* CSS Grid: rows are sections (digital, phase header, physical, ideas), cols are phases */}
          <div
            ref={mapRef}
            id="journey-print-area"
            className="inline-grid gap-x-4 gap-y-3 pb-4"
            style={{
              gridTemplateColumns: phases.map(() => '288px').join(' 32px '),
              gridTemplateRows: 'auto auto auto auto',
              alignItems: 'start',
            }}
          >
            {phases.map((phase, idx) => (
              <div key={phase.id} style={{ display: 'contents' }}>
                {/* Row 1: Digital */}
                <div style={{ gridColumn: idx * 2 + 1, gridRow: 1 }}
                  className="bg-white rounded-xl border border-indigo-100 shadow-sm flex flex-col gap-2 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Globe className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Digitální</span>
                    </div>
                    <button onClick={() => setModal({ kind: 'touchpoint', phaseId: phase.id, defaultType: 'digital' })}
                      data-section-add
                      className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-lg transition-colors">
                      <Plus className="w-3 h-3" /> Přidat
                    </button>
                  </div>
                  <DroppableColumn id={`col-digital-${phase.id}`} className="flex flex-col gap-2 min-h-[60px]">
                    <SortableContext items={phase.digitalTouchpoints.map(t => t.id)} strategy={verticalListSortingStrategy}>
                      {phase.digitalTouchpoints.map(tp => (
                        <SortableTouchpoint key={tp.id} touchpoint={tp} phaseColor={phase.color} phaseBg={phase.headerBg}
                          onToggleInfluence={id => handleToggleInfluence(phase.id, id)}
                          onDelete={id => handleDeleteTouchpoint(phase.id, id)}
                          onRename={(id, title) => handleRenameTouchpoint(phase.id, id, title)} />
                      ))}
                      {phase.digitalTouchpoints.length === 0 && (
                        <div className="text-xs text-gray-300 text-center py-4 border-2 border-dashed border-gray-100 rounded-lg">Přetáhněte sem</div>
                      )}
                    </SortableContext>
                  </DroppableColumn>
                </div>

                {/* Row 2: Phase header */}
                <div style={{ gridColumn: idx * 2 + 1, gridRow: 2 }}
                  className={`${phase.headerBg} ${phase.color} border-2 rounded-xl px-4 py-3 text-center`}>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fáze {phase.order}</span>
                  <h3 className="text-base font-extrabold text-gray-800 mt-0.5">{phase.name}</h3>
                </div>

                {/* Row 3: Physical */}
                <div style={{ gridColumn: idx * 2 + 1, gridRow: 3 }}
                  className="bg-white rounded-xl border border-emerald-100 shadow-sm flex flex-col gap-2 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Store className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Fyzické</span>
                    </div>
                    <button onClick={() => setModal({ kind: 'touchpoint', phaseId: phase.id, defaultType: 'physical' })}
                      data-section-add
                      className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded-lg transition-colors">
                      <Plus className="w-3 h-3" /> Přidat
                    </button>
                  </div>
                  <DroppableColumn id={`col-physical-${phase.id}`} className="flex flex-col gap-2 min-h-[60px]">
                    <SortableContext items={phase.physicalTouchpoints.map(t => t.id)} strategy={verticalListSortingStrategy}>
                      {phase.physicalTouchpoints.map(tp => (
                        <SortableTouchpoint key={tp.id} touchpoint={tp} phaseColor={phase.color} phaseBg={phase.headerBg}
                          onToggleInfluence={id => handleToggleInfluence(phase.id, id)}
                          onDelete={id => handleDeleteTouchpoint(phase.id, id)}
                          onRename={(id, title) => handleRenameTouchpoint(phase.id, id, title)} />
                      ))}
                      {phase.physicalTouchpoints.length === 0 && (
                        <div className="text-xs text-gray-300 text-center py-4 border-2 border-dashed border-gray-100 rounded-lg">Přetáhněte sem</div>
                      )}
                    </SortableContext>
                  </DroppableColumn>
                </div>

                {/* Row 4: Ideas */}
                <div data-pdf-hide style={{ gridColumn: idx * 2 + 1, gridRow: 4 }}
                  className="bg-white rounded-xl border border-amber-100 shadow-sm flex flex-col gap-2 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Nápady</span>
                    </div>
                    <button onClick={() => setModal({ kind: 'idea', phaseId: phase.id })}
                      data-section-add
                      className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-lg transition-colors">
                      <Plus className="w-3 h-3" /> Přidat
                    </button>
                  </div>
                  <SortableContext items={phase.ideas.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <div className="flex flex-col gap-2 min-h-[40px]">
                      {phase.ideas.map(idea => (
                        <SortableIdea key={idea.id} idea={idea}
                          onClickMobile={() => setModal({ kind: 'assignIdea', ideaId: idea.id, ideaTitle: idea.title })} />
                      ))}
                      {phase.ideas.length === 0 && (
                        <div className="text-xs text-gray-300 text-center py-3 border-2 border-dashed border-gray-100 rounded-lg">
                          Přetáhněte nebo klikněte
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </div>

                {/* Arrow between phases (rows 1-3) */}
                {idx < phases.length - 1 && (
                  <div style={{ gridColumn: idx * 2 + 2, gridRow: '1 / 4' }}
                    data-pdf-arrow
                    className="flex items-center justify-center">
                    <ArrowRight className="w-5 h-5 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Drag overlay */}
          <DragOverlay>
            {overlayTouchpoint && (
              <TouchpointCard
                id={overlayTouchpoint.id}
                title={overlayTouchpoint.title}
                type={overlayTouchpoint.type}
                canInfluence={overlayTouchpoint.canInfluence}
                phaseColor={activePhase?.color}
                phaseBg={activePhase?.headerBg}
                onToggleInfluence={() => {}}
                onDelete={() => {}}
              />
            )}
            {overlayIdea && (
              <IdeaCard id={overlayIdea.id} title={overlayIdea.title} source={overlayIdea.source} isDragging />
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Modals */}
      {modal.kind === 'touchpoint' && (
        <AddTouchpointModal
          isOpen
          onClose={() => setModal({ kind: 'none' })}
          phaseName={phases.find(p => p.id === modal.phaseId)?.name}
          phaseId={modal.phaseId}
          defaultType={modal.defaultType}
          onAdd={({ name, type }) => handleAddTouchpoint(name, type, modal.phaseId)}
        />
      )}
      {modal.kind === 'idea' && (
        <AddIdeaModal
          isOpen
          onClose={() => setModal({ kind: 'none' })}
          onAdd={({ title, description }) => handleAddIdea(title, description, modal.phaseId)}
        />
      )}
      {modal.kind === 'assignIdea' && (
        <AssignIdeaModal
          isOpen
          onClose={() => setModal({ kind: 'none' })}
          ideaTitle={modal.ideaTitle}
          phases={phaseListForAssign}
          onAssign={({ phaseId, type }) => handleAssignIdea(modal.ideaId, phaseId, type)}
        />
      )}
    </div>
  );
};

export default JourneyMapPage;
